import type { FieldDef, FieldType } from '../../../form-engine';
import { locate } from './definitionOps';

export type PaletteGroup = 'structure' | 'text' | 'choice';

/** A control of the palette: a field type, possibly with a preset display (e.g. a dropdown = SINGLE_CHOICE "select"). */
export interface PaletteItem {
  id: string;
  type: FieldType;
  group: PaletteGroup;
  display?: 'select';
}

export const PALETTE: PaletteItem[] = [
  { id: 'section', type: 'SECTION', group: 'structure' },
  { id: 'repeatingGroup', type: 'REPEATING_GROUP', group: 'structure' },
  { id: 'info', type: 'INFO', group: 'structure' },
  { id: 'text', type: 'TEXT', group: 'text' },
  { id: 'textarea', type: 'TEXTAREA', group: 'text' },
  { id: 'richText', type: 'RICH_TEXT', group: 'text' },
  { id: 'table', type: 'TABLE', group: 'text' },
  { id: 'autocomplete', type: 'AUTOCOMPLETE', group: 'text' },
  { id: 'number', type: 'NUMBER', group: 'text' },
  { id: 'date', type: 'DATE', group: 'text' },
  { id: 'radios', type: 'SINGLE_CHOICE', group: 'choice' },
  { id: 'checkboxes', type: 'MULTI_CHOICE', group: 'choice' },
  { id: 'switch', type: 'SWITCH', group: 'choice' },
  { id: 'yesNo', type: 'YES_NO', group: 'choice' },
  { id: 'select', type: 'SINGLE_CHOICE', group: 'choice', display: 'select' },
  { id: 'multiSelect', type: 'MULTI_CHOICE', group: 'choice', display: 'select' },
  { id: 'teamMember', type: 'TEAM_MEMBER', group: 'choice' },
];

export const PALETTE_GROUPS: PaletteGroup[] = ['structure', 'text', 'choice'];

/** Where a new control goes: the end of the form (null) or the end of a section / repeating group. */
export interface PaletteTarget {
  key: string | null;
  type: FieldType | null;
  label: string;
}

/** No sections in sections; no sections or repeating groups in repeating groups (same rule as the validators). */
export function allowedIn(type: FieldType, parentType: FieldType | null): boolean {
  if (parentType === 'SECTION') return type !== 'SECTION';
  if (parentType === 'REPEATING_GROUP') return type !== 'SECTION' && type !== 'REPEATING_GROUP';
  return true;
}

/** Sections and repeating groups a control can be added to (custom mode: only the team's own). */
export function groupTargets(fields: FieldDef[], editable: (f: FieldDef) => boolean): FieldDef[] {
  const out: FieldDef[] = [];
  const walk = (list: FieldDef[]) => {
    for (const f of list) {
      if ((f.type === 'SECTION' || f.type === 'REPEATING_GROUP') && editable(f)) out.push(f);
      walk(f.children ?? []);
    }
  };
  walk(fields);
  return out;
}

/** The query string of a new-field route: `?zobrazenie=select&skupina=<key>&poradie=<n>` (n from 1). */
export function paletteQuery(item: PaletteItem, parentKey: string | null, index?: number): string {
  const params = new URLSearchParams();
  if (item.display) params.set('zobrazenie', item.display);
  if (parentKey) params.set('skupina', parentKey);
  if (index !== undefined) params.set('poradie', String(index + 1));
  const q = params.toString();
  return q ? `?${q}` : '';
}

/** `?poradie=<n>` of a new-field route as an index from 0; null without one (the end of the list). */
export function positionParam(params: URLSearchParams): number | null {
  const raw = params.get('poradie');
  return raw && /^[1-9]\d{0,3}$/.test(raw) ? Number(raw) - 1 : null;
}

/** A place for a new control: index `index` of the top level (null) or of the fields of a section / repeating group. */
export interface DropPosition {
  parentKey: string | null;
  index: number;
}

/**
 * A place for a dropped control or field and how it is shown: a line at an edge of the hovered row, or the hovered
 * section / repeating group outlined as a whole ("into the group, at its end").
 */
export interface DropTarget {
  position: DropPosition;
  mark: { kind: 'line'; edge: 'top' | 'bottom' } | { kind: 'group' };
}

export const isGroup = (f: FieldDef) => f.type === 'SECTION' || f.type === 'REPEATING_GROUP';

/**
 * Where a control or field dropped on the row of field `key` goes; `y` is the pointer height in the row (0 top, 1
 * bottom) and `fits` tells whether it may go among the fields of a group (null: the top level).
 *
 * - A field row: the upper half means before the field, the lower half after it.
 * - A section or repeating group: the top quarter means before it; below that, a drop on the group puts it into the
 *   group, at the end of its fields. Where it cannot go into the group, it goes before the group (after an empty one).
 * - Below the last field of a group it cannot go into, it goes after the group.
 *
 * Null: no drop on this row.
 */
export function resolveDrop(fields: FieldDef[], key: string, y: number, fits: (group: FieldDef | null) => boolean): DropTarget | null {
  const loc = locate(fields, key);
  if (!loc) return null;
  const line = (parent: FieldDef | null, index: number, edge: 'top' | 'bottom'): DropTarget | null =>
    fits(parent) ? { position: { parentKey: parent?.key ?? null, index }, mark: { kind: 'line', edge } } : null;
  const f = loc.field;
  if (isGroup(f)) {
    if (y < 0.25) return line(loc.parent, loc.index, 'top');
    if (fits(f)) return { position: { parentKey: f.key, index: f.children?.length ?? 0 }, mark: { kind: 'group' } };
    // its fields follow its row: a line below the row would mean "inside"
    if (f.children?.length) return line(loc.parent, loc.index, 'top');
  } else if (y < 0.5) {
    return line(loc.parent, loc.index, 'top');
  }
  let parent = loc.parent;
  let index = loc.index + 1;
  let siblings = loc.siblings;
  while (parent && !fits(parent) && index === siblings.length) {
    const up = locate(fields, parent.key)!;
    parent = up.parent;
    index = up.index + 1;
    siblings = up.siblings;
  }
  return line(parent, index, 'bottom');
}

/**
 * Where an existing field may be moved: as a new control of its type, and fields of a repeating group only within
 * their group (their values are row values) – fields outside repeating groups never into one; never into itself.
 */
export function moveFits(fields: FieldDef[], key: string, canDropIn: (group: FieldDef | null) => boolean): (group: FieldDef | null) => boolean {
  const loc = locate(fields, key);
  if (!loc) return () => false;
  const rows = loc.parent?.type === 'REPEATING_GROUP' ? loc.parent.key : null;
  const inside = new Set<string>();
  const walk = (f: FieldDef) => {
    inside.add(f.key);
    (f.children ?? []).forEach(walk);
  };
  walk(loc.field);
  return (group) => {
    if (!canDropIn(group) || !allowedIn(loc.field.type, group?.type ?? null)) return false;
    if (group && inside.has(group.key)) return false;
    return rows ? group?.key === rows : group?.type !== 'REPEATING_GROUP';
  };
}

/** A move of field `key` to `position` that leaves it where it is. */
export function staysInPlace(fields: FieldDef[], key: string, position: DropPosition): boolean {
  const loc = locate(fields, key);
  if (!loc || (loc.parent?.key ?? null) !== position.parentKey) return false;
  return position.index === loc.index || position.index === loc.index + 1;
}

/** Nesting depth of the fields of `parentKey` (0: the top level). */
export function depthOf(fields: FieldDef[], parentKey: string | null): number {
  let depth = 0;
  let loc = parentKey ? locate(fields, parentKey) : null;
  while (loc) {
    depth++;
    loc = loc.parent ? locate(fields, loc.parent.key) : null;
  }
  return depth;
}
